import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";
import ListGroup from "react-bootstrap/ListGroup";
import Badge from "react-bootstrap/Badge";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import LessonChatWidget from "../components/LessonChatWidget";
import ModuleCompletionModal from "../components/ModuleCompletionModal";
import { useTranslation } from "../hooks/useTranslation";

import {
  BookOpen,
  FileText,
  ArrowRight,
  ArrowLeft,
  GraduationCap,
  ChevronLeft,
} from "lucide-react";
import {
  getModule,
  listModules,
  getMe,
  getLessonProgress,
  upsertLessonProgress,
} from "../api/client";



const brandBlue = "#1E88E5";

export default function ModulePage() {
  const { t } = useTranslation();
  const { moduleId, lessonId } = useParams();
  const navigate = useNavigate();

  const [moduleData, setModuleData] = useState(null);
  const [modules, setModules] = useState([]);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [user, setUser] = useState(null);
  const [lessonProgress, setLessonProgress] = useState([]);

  const effectiveModuleId = moduleId || "M1_WR_1K";
  const isStandaloneLesson = Boolean(lessonId);

  const [showCompletionModal, setShowCompletionModal] = useState(false);

  // Modul + Modulliste laden
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        const allModules = await listModules();
        const sorted = (Array.isArray(allModules) ? allModules : []).sort(
          (a, b) => (a.order_index || 0) - (b.order_index || 0)
        );
        setModules(sorted);

        const data = await getModule(effectiveModuleId);
        if (!data || !Array.isArray(data.lessons)) {
          throw new Error("invalid_module_structure");
        }
        setModuleData(data);

        const initialLessonId = lessonId || data.lessons[0]?.id || null;
        setActiveLessonId(initialLessonId);
      } catch (e) {
        console.error("ModulePage load error:", e);
        setError(t.modules.moduleLoadError);
      } finally {
        setLoading(false);
      }
    })();
  }, [effectiveModuleId, lessonId, t]);

  // User + Lesson-Progress laden
  useEffect(() => {
    (async () => {
      try {
        const me = await getMe();
        if (!me || !me.id) return;
        setUser(me);

        const lp = await getLessonProgress(me.id);
        setLessonProgress(Array.isArray(lp) ? lp : []);
      } catch (e) {
        console.error("ModulePage user/progress load error:", e);
      }
    })();
  }, []);

  const handleBackToModule = () => {
    navigate(`/Lessons?module=${effectiveModuleId}`);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-2 text-muted">{t.modules.moduleLoading}</div>
      </Container>
    );
  }

  if (error || !moduleData) {
    return (
      <Container className="py-5 text-center">
        <p className="text-danger mb-3">
          {error || t.modules.unknownError}
        </p>
        <p className="text-muted mb-3">
          {t.modules.tryAgainLater}
        </p>
        <Link to="/Lessons">
          <Button variant="outline-primary">{t.modules.backToLessons}</Button>
        </Link>
      </Container>
    );
  }

  const activeLesson =
    moduleData.lessons.find((l) => l.id === activeLessonId) ||
    moduleData.lessons[0];

  const lessonIndex =
    moduleData.lessons.findIndex((l) => l.id === activeLesson?.id) ?? 0;

  // Progress-Record für aktuelle Lesson
  const currentLessonProgress =
    user && activeLesson
      ? lessonProgress.find(
          (p) => p.lesson_id === activeLesson.id && p.user_id === user.id
        ) || null
      : null;

  const isLessonCompleted = currentLessonProgress?.status === "completed";

  const goPrevLesson = () => {
    if (lessonIndex > 0) {
      setActiveLessonId(moduleData.lessons[lessonIndex - 1].id);
    }
  };

  // Lektion als fertig markieren + zur nächsten springen
  const handleCompleteAndNext = async () => {
    if (!user || !activeLesson) return;

    const now = new Date();
    const completedDate = now.toISOString().slice(0, 19).replace("T", " ");

    try {
      await upsertLessonProgress({
        id: currentLessonProgress?.id || undefined,
        lesson_id: activeLesson.id,
        user_id: user.id,
        status: "completed",
        progress_percent: 100,
        time_spent_minutes: currentLessonProgress?.time_spent_minutes || 0,
        completed_date: completedDate,
      });

      setLessonProgress((prev) => {
        const others = prev.filter(
          (p) => !(p.lesson_id === activeLesson.id && p.user_id === user.id)
        );
        return [
          ...others,
          {
            ...(currentLessonProgress || {}),
            id: currentLessonProgress?.id || `local_${Date.now()}`,
            lesson_id: activeLesson.id,
            user_id: user.id,
            status: "completed",
            progress_percent: 100,
            time_spent_minutes: currentLessonProgress?.time_spent_minutes || 0,
            completed_date: completedDate,
          },
        ];
      });

      if (lessonIndex === moduleData.lessons.length - 1) {
        // LETZTE LEKTION - Modal öffnen
        console.log("Opening completion modal!");
        setShowCompletionModal(true);
      } else {
        // Zur nächsten Lektion springen
        setActiveLessonId(moduleData.lessons[lessonIndex + 1].id);
      }
    } catch (e) {
      console.error("ModulePage upsertLessonProgress error:", e);
    }
  };


  // Helper: Schwierigkeitsgrad Label
  const getDifficultyLabel = (difficulty) => {
    const labels = {
      de: { beginner: 'Anfänger', intermediate: 'Fortgeschritten', advanced: 'Experte' },
      en: { beginner: 'Beginner', intermediate: 'Intermediate', advanced: 'Advanced' },
    };
    return labels[t._lang]?.[difficulty] || difficulty;
  };

  const getDifficultyVariant = (difficulty) => {
    const variants = { beginner: 'success', intermediate: 'warning', advanced: 'danger' };
    return variants[difficulty] || 'secondary';
  };

  // Helper: Kategorie Label
  const getCategoryLabel = (category) => {
    const labels = {
      de: {
        grundlagen: 'Grundlagen',
        bilanzierung: 'Bilanzierung',
        kostenrechnung: 'Kostenrechnung',
        controlling: 'Controlling',
        steuern: 'Steuern',
      },
      en: {
        grundlagen: 'Fundamentals',
        bilanzierung: 'Accounting',
        kostenrechnung: 'Cost Accounting',
        controlling: 'Controlling',
        steuern: 'Taxes',
      },
    };
    return labels[t._lang]?.[category] || category;
  };

  return (
    <>

      {/* Main Content */}
      <main
        className="modulepage-main"
        style={{
          background: "#f8f9fa",
          minHeight: "100vh",
        }}
      >
        <Container
          className="py-4"
          style={{
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          {/* Zurück-Button (nur in Standalone-Ansicht) */}
          {isStandaloneLesson && (
            <div className="mb-3">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={handleBackToModule}
                className="d-flex align-items-center gap-2"
              >
                <ChevronLeft size={18} />
                {t.modules.backToModule}
              </Button>
            </div>
          )}

          {/* Kopfbereich */}
          <div className="mb-4">
            <div className="d-flex align-items-center gap-3 mb-2">
              <div
                className="rounded-3 d-flex align-items-center justify-content-center"
                style={{
                  width: 56,
                  height: 56,
                  backgroundColor: "rgba(30,136,229,0.1)",
                }}
              >
                <GraduationCap size={30} style={{ color: brandBlue }} />
              </div>
              <div>
                <h1 className="mb-1">{moduleData.title}</h1>
                <div className="d-flex flex-wrap gap-2">
                  {moduleData.year_level && (
                    <Badge bg="primary">
                      {moduleData.year_level}. {t.modules.class} HAK
                    </Badge>
                  )}
                  {moduleData.semester && (
                    <Badge bg="info">{t.modules.semester} {moduleData.semester}</Badge>
                  )}
                </div>
              </div>
            </div>
            {moduleData.description && !isStandaloneLesson && (
              <p className="text-muted mb-0">{moduleData.description}</p>
            )}
          </div>

          {/* Content */}
          <Row className="g-3">
            {/* Lektionen-Sidebar (nur in Modul-Ansicht) */}
            {!isStandaloneLesson && (
              <Col lg={4}>
                <Card className="border-0 shadow-sm mb-3">
                  <Card.Body>
                    <div className="d-flex align-items-center mb-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-2"
                        style={{
                          width: 36,
                          height: 36,
                          backgroundColor: "rgba(30,136,229,0.15)",
                        }}
                      >
                        <BookOpen size={20} style={{ color: brandBlue }} />
                      </div>
                      <div>
                        <h5 className="mb-0">{t.modules.lessonsInModule}</h5>
                        <small className="text-muted">
                          {moduleData.lessons.length} {moduleData.lessons.length === 1 ? t.modules.lessonSingular : t.modules.lessonPlural}
                        </small>
                      </div>
                    </div>

                    <ListGroup variant="flush">
                      {moduleData.lessons.map((lesson, idx) => {
                        const isActive = lesson.id === activeLesson?.id;
                        return (
                          <ListGroup.Item
                            key={lesson.id}
                            action
                            onClick={() => setActiveLessonId(lesson.id)}
                            className="d-flex justify-content-between align-items-start"
                            style={{
                              borderRadius: 8,
                              marginBottom: 4,
                              backgroundColor: isActive
                                ? "rgba(30,136,229,0.1)"
                                : "transparent",
                              border: "none",
                            }}
                          >
                            <div className="me-2">
                              <div className="d-flex align-items-center mb-1">
                                <Badge
                                  bg={isActive ? "primary" : "secondary"}
                                  className="me-2"
                                >
                                  {t.lessons.lesson} {idx + 1}
                                </Badge>
                                <span
                                  style={{
                                    fontWeight: isActive ? 600 : 500,
                                  }}
                                >
                                  {lesson.title}
                                </span>
                              </div>
                              {lesson.description && (
                                <small className="text-muted">
                                  {lesson.description}
                                </small>
                              )}
                            </div>
                          </ListGroup.Item>
                        );
                      })}
                    </ListGroup>
                  </Card.Body>
                </Card>
              </Col>
            )}

            {/* Lesson Content */}
            <Col lg={isStandaloneLesson ? 12 : 8}>
              <Card className="border-0 shadow-sm mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                        <h3 className="mb-0">{activeLesson.title}</h3>

                        {activeLesson.category && (
                          <Badge bg="light" text="dark">
                            {getCategoryLabel(activeLesson.category)}
                          </Badge>
                        )}

                        {activeLesson.difficulty && (
                          <Badge bg={getDifficultyVariant(activeLesson.difficulty)}>
                            {getDifficultyLabel(activeLesson.difficulty)}
                          </Badge>
                        )}
                      </div>

                      {activeLesson.learning_objectives &&
                        activeLesson.learning_objectives.length > 0 && (
                          <div className="text-muted small">
                            {t.lessons.learningGoals}
                            <ul className="mb-1">
                              {activeLesson.learning_objectives.map((obj) => (
                                <li key={obj}>{obj}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>

                    {!isStandaloneLesson && (
                      <div className="text-end">
                        <small className="text-muted d-block mb-1">
                          {t.modules.lessonOf
                            .replace('{{current}}', lessonIndex + 1)
                            .replace('{{total}}', moduleData.lessons.length)}
                        </small>
                        <div className="d-flex gap-2 justify-content-end">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={goPrevLesson}
                            disabled={lessonIndex === 0}
                          >
                            <ArrowLeft size={16} className="me-1" />
                            {t.modules.back}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Theorie */}
                  {Array.isArray(activeLesson.content_sections) &&
                    activeLesson.content_sections.length > 0 && (
                      <div className="mb-2">
                        <div className="d-flex align-items-center mb-2">
                          <FileText
                            size={18}
                            className="me-2 text-secondary"
                          />
                          <h5 className="mb-0">{t.lessons.theory}</h5>
                        </div>
                        {activeLesson.content_sections.map((section) => (
                          <div key={section.id} className="mb-3">
                            {section.title && (
                              <h6 className="fw-semibold mb-1">
                                {section.title}
                              </h6>
                            )}
                            <div className="bg-light rounded p-3">
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm, remarkMath]}
                                rehypePlugins={[rehypeKatex]}
                                components={{
                                  h1: (props) => <h5 className="mt-2 mb-2" {...props} />,
                                  h2: (props) => <h6 className="mt-2 mb-2" {...props} />,
                                  ul: (props) => (
                                    <ul
                                      className="mb-2"
                                      style={{ paddingLeft: "1.2rem" }}
                                      {...props}
                                    />
                                  ),
                                  table: ({ node, ...props }) => (
                                    <div className="lesson-table-wrapper table-responsive">
                                      <table className="table table-sm mb-0" {...props} />
                                    </div>
                                  ),
                                }}
                              >
                                {section.markdown}
                              </ReactMarkdown>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                  {/* Aktionen unten: Nächste Lektion */}
                  <div className="d-flex justify-content-end mt-3">
                    <Button
                      variant={isLessonCompleted ? "success" : "primary"}
                      size="sm"
                      onClick={handleCompleteAndNext}
                    >
                      {lessonIndex === moduleData.lessons.length - 1
                        ? isLessonCompleted
                          ? t.modules.completeLesson
                          : t.modules.finishModule
                        : isLessonCompleted
                        ? t.modules.completeAndNext
                        : t.modules.completeAndNext}
                      <ArrowRight size={16} className="ms-1" />
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </main>

      {/* AI Chat Widget */}
      <LessonChatWidget
        lessonContext={{
          lessonId: activeLesson?.id,
          title: activeLesson?.title,
          description: activeLesson?.description,
          moduleTitle: moduleData?.title,
          content:
            activeLesson?.content_sections
              ?.map((s) => s.markdown)
              .join("\n\n") || "",
        }}
      />

      <ModuleCompletionModal
        show={showCompletionModal}
        onHide={() => setShowCompletionModal(false)}
        moduleData={moduleData}
        completedLessonsCount={moduleData?.lessons?.length || 0}
        userId={user?.id}
      />
    </>
  );
}
